/* eslint-disable complexity */
import {useEffect, useLayoutEffect} from 'react';

import Ajv from 'ajv';
import chroma from 'chroma-js';
import {Theme} from 'common/constants';
import {AlignControlValue, Style} from 'common/constants/survey';
import {PostMessageStyles, SurveyCustomStyles, SurveyStyles} from 'common/models/survey';
import {isNil, isNumber} from 'lodash';
import {MessageType} from 'ui/constants/MessageType';
import {useAppDispatch} from 'ui/store';
import {setThemeAction} from 'ui/store/system/actions/setTheme';
import isEmpty from 'ui/utils/isEmpty';
import * as PostMessage from 'ui/utils/postMessages';

import {
    BLACK,
    GRAY,
    MIN_CONTRAST_RATIO,
    WHITE,
    alignToFlexMap,
    imagePath,
    validationSchema,
} from '../constants';

const {preview} = window.YC;

const ajv = new Ajv({removeAdditional: 'all', allErrors: true});
const validate = ajv.compile(validationSchema);
const root = document.querySelector('#root') as HTMLElement;

const isUrlIncludesPlugin = (url?: string) => typeof url === 'string' && url.includes('@plugin');

const clearUrl = (url?: string) => {
    if (isUrlIncludesPlugin(url)) {
        return undefined;
    }
    // url pattern in ajv validation scheme must be case insensitive
    return JSON.stringify(url).toLowerCase();
};

const fixUrlPath = (url?: string) => {
    if (url && !/^(https?:)?\/\//.test(url)) {
        return imagePath + url.match(/\/[^/]+$/);
    }
    return url;
};

const prepareImages = ({custom, images}: SurveyStyles): SurveyCustomStyles => {
    const styles: SurveyCustomStyles = {...custom};

    const formImageUrl = images?.form?.links?.['2560x'] || styles[Style.BackgroundUrl];
    if (formImageUrl) {
        styles[Style.BackgroundUrl] = fixUrlPath(formImageUrl);
    }

    const pageImageUrl = images?.page?.links?.['2560x'] || styles[Style.PageBackgroundUrl];
    if (pageImageUrl) {
        styles[Style.PageBackgroundUrl] = fixUrlPath(pageImageUrl);
    }

    return styles;
};

const validateStyles = (styles: SurveyCustomStyles): SurveyCustomStyles => {
    if (preview) {
        if (styles[Style.BackgroundUrl]) {
            styles[Style.BackgroundUrl] = clearUrl(styles[Style.BackgroundUrl]);
        }
        if (styles[Style.PageBackgroundUrl]) {
            styles[Style.PageBackgroundUrl] = clearUrl(styles[Style.PageBackgroundUrl]);
        }
        const isValid = validate(styles);
        if (!isValid) {
            validate.errors?.forEach((error) => {
                const key = error.instancePath.substring(1);
                if (styles[key as Style]) {
                    delete styles[key as Style];
                }
            });
        }
    }

    return styles;
};

const getStyleVariable = (style: string) => {
    return '--custom-' + style.split('_').join('-');
};

const prepareValue = (style: Style, value: string | number) => {
    switch (style) {
        case Style.FormPadding:
            return Number(value) + 'px';
        case Style.ButtonBorderRadius:
            return value + 'px';
        case Style.FormBorderRadius:
            return Number(value) + 'px';
        case Style.PageBackgroundUrl:
        case Style.BackgroundUrl:
            return `url(${value})`;
        case Style.AlignControls:
            return alignToFlexMap[value as AlignControlValue] ?? 'start';
        default:
            return String(value);
    }
};

const addDependentStyles = (validStyles: SurveyCustomStyles): SurveyCustomStyles => {
    const styles: SurveyCustomStyles = {...validStyles};

    if (!styles[Style.AnswerTextColor]) {
        styles[Style.AnswerTextColor] = styles[Style.QuestionTextColor];
    }

    if (styles[Style.QuestionTextColor] && chroma.valid(styles[Style.QuestionTextColor])) {
        styles[Style.SecondaryTextColor] = chroma
            .mix(styles[Style.QuestionTextColor], WHITE, 0.5, 'rgb')
            .toString();
        styles[Style.HintTextColor] = chroma
            .mix(styles[Style.QuestionTextColor], WHITE, 0.7, 'rgb')
            .toString();
        styles[Style.QuestionLineColor] = chroma
            .mix(styles[Style.QuestionTextColor], WHITE, 0.85, 'rgb')
            .toString();
        styles[Style.SeriesBackgroundColor] = chroma(styles[Style.QuestionTextColor])
            .alpha(0.15)
            .css();
    }

    if (styles[Style.AnswerTextColor] && chroma.valid(styles[Style.AnswerTextColor])) {
        styles[Style.AnswerLineColor] = chroma
            .mix(styles[Style.AnswerTextColor], WHITE, 0.85, 'rgb')
            .toString();
        styles[Style.AnswerLineHoverColor] = chroma
            .mix(styles[Style.AnswerTextColor], WHITE, 0.7, 'rgb')
            .toString();
    }

    if (styles[Style.ControlsColor] && chroma.valid(styles[Style.ControlsColor])) {
        const background = chroma(styles[Style.ControlsColor]);
        const isBackgroundDark = chroma.contrast(background, WHITE) >= MIN_CONTRAST_RATIO;
        styles[Style.ControlsTextColor] = isBackgroundDark ? 'white' : 'black';
        styles[Style.ControlsColorHover] = isBackgroundDark
            ? background.mix(WHITE, 0.02).toString()
            : background.mix(BLACK, 0.3).toString();
    }

    if (styles[Style.ControlOpacity] && isNumber(styles[Style.ControlOpacity])) {
        styles[Style.FieldBackgroundColor] = WHITE.alpha(styles[Style.ControlOpacity]).css();
        styles[Style.StarsBackgroundColor] = WHITE.css();
        styles[Style.StarsBackgroundColorHover] = GRAY.css();
    }

    return styles;
};

const applyStyles = (styles: SurveyCustomStyles) => {
    for (const [style, value] of Object.entries(styles)) {
        if (!isNil(value)) {
            root.style.setProperty(getStyleVariable(style), prepareValue(style as Style, value));
        }
    }
};

const setStyles = (styles: SurveyStyles) => {
    const finalStyles = addDependentStyles(validateStyles(prepareImages(styles)));
    if (!isEmpty(finalStyles)) {
        applyStyles(finalStyles);
    }
};

const fixFormat = (data: PostMessageStyles): SurveyStyles => {
    return {
        custom: data.styles,
        images: {
            page: data.image_page,
            form: data.image_form,
        },
    };
};

const clearStyles = () => {
    root.removeAttribute('style');
};

const setPostMessageStyles = ({data: {data, type}}: MessageEvent) => {
    switch (type) {
        case MessageType.SetStylesTemplate: {
            clearStyles();
            setStyles(fixFormat(data));
            break;
        }
        case MessageType.ClearStyles: {
            clearStyles();
            break;
        }
    }
};

const useStyles = (styles?: SurveyStyles) => {
    const dispatch = useAppDispatch();

    useLayoutEffect(() => {
        if (styles) {
            dispatch(setThemeAction(Theme.Light));
            setStyles(styles);
        }
    }, [styles]);

    useEffect(() => {
        if (preview) {
            window.addEventListener('message', setPostMessageStyles);
            PostMessage.sendStylesReady();
        }
        return () => window.removeEventListener('message', setPostMessageStyles);
    }, []);
};
export default useStyles;
